import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import React from 'react';

import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  children?: React.ReactNode,
  className?: string,
  logo?: React.ReactNode,
  mode?: string,
  percentage?: number,
  size: number,
  withProgressBar?: boolean,
};

const DATA_TEST_NAMESPACE = 'circular-progress-bar';

export const CircularProgressBar = withSpacingStyles(
  ({ children, className, logo, mode, percentage = 0, size, withProgressBar = true }: Props) => {
    const { activeProgressColor, backgroundColor, inactiveProgressColor, progressWidth, roundedProgress } =
      useComponentTheme(theme => theme.components.CircularProgressBar, mode);
    const theme = useTheme();
    const borderWidth = progressWidth || size / 16;
    const logoSize = withProgressBar ? '50%' : '100%';

    return (
      <ProgressBarContainer className={className} data-test={DATA_TEST_NAMESPACE} size={size}>
        {withProgressBar && (
          <svg data-test={`${DATA_TEST_NAMESPACE}-circle`} height="100%" width="100%">
            <title data-test={`${DATA_TEST_NAMESPACE}-title`}>{percentage}%</title>

            <Circle
              cx="50%"
              cy="50%"
              fill={backgroundColor}
              outline={`${borderWidth}px solid ${inactiveProgressColor || transparentize(0.75, theme.color.brand)}`}
              r={(size - (borderWidth * 2)) / 2}
            />

            <ProgressBar
              cx="50%"
              cy="50%"
              data-test={`${DATA_TEST_NAMESPACE}-percentage`}
              fill="transparent"
              pathLength="100"
              r={(size - borderWidth) / 2}
              stroke={activeProgressColor || theme.color.brand}
              strokeDasharray={percentage === 100 ? '100 0' : `${percentage} 100`}
              strokeWidth={borderWidth}
              {...(roundedProgress && percentage && { strokeLinecap: 'round' })}
            />
          </svg>
        )}

        {logo && <LogoWrapper data-test={`${DATA_TEST_NAMESPACE}-logo-wrapper`} size={logoSize}>{logo}</LogoWrapper>}
        {children && <Content size={size - (borderWidth * 2)}>{children}</Content>}
      </ProgressBarContainer>
    );
  }
);

const Circle = styled.circle(({ outline }) => `
  border-radius: 50%;
  outline: ${outline};
`);

const Content = styled('div', { shouldForwardProp: prop => prop !== 'size' })(({ size }) => `
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  left: 50%;
  top: 50%;
  width: ${size}px;
  height: ${size}px;
  transform: translate(-50%, -50%);
`);

const LogoWrapper = styled('div')(({ size }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${size};
  height: ${size};
  transform: translate(-50%, -50%);
`);

const ProgressBar = styled.circle`
  transform: rotate(-90deg);
  transform-origin: center;
  transition: stroke-dasharray 0.2s linear;
`;

const ProgressBarContainer = styled('div', { shouldForwardProp: prop => prop !== 'size' })(({ size }) => `
  position: relative;
  width: ${size}px;
  height: ${size}px;
  ${size === 96 ? `margin-bottom: 16px;` : ''}
`);
